import { useEffect, useState } from "react";
import { Inbox, Mail, Phone, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Navbar, loginWithGoogle } from "./Navbar";
import { Footer } from "./Footer";
import { Reveal, Eyebrow } from "./Reveal";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", quotes: [] });

  const load = () => {
    fetch(`${API}/quotes`, { credentials: "include" })
      .then(async (r) => {
        if (r.status === 401) return { status: "needAuth", quotes: [] };
        if (r.status === 403) return { status: "forbidden", quotes: [] };
        if (!r.ok) throw new Error();
        return { status: "ok", quotes: await r.json() };
      })
      .then(setState)
      .catch(() => setState({ status: "error", quotes: [] }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (user) load();
    if (user === null) setState({ status: "needAuth", quotes: [] });
  }, [user]);

  const remove = async (quoteId) => {
    const r = await fetch(`${API}/quotes/${quoteId}`, { method: "DELETE", credentials: "include" });
    if (r.ok) {
      setState((s) => ({ ...s, quotes: s.quotes.filter((q) => q.quote_id !== quoteId) }));
      toast.success("Richiesta eliminata.");
    } else {
      toast.error("Impossibile eliminare la richiesta.");
    }
  };

  return (
    <div className="grain bg-[#070D18] min-h-screen" data-testid="admin-page">
      <Navbar />
      <main className="pt-40 pb-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Area riservata</Eyebrow>
            <h1 className="mt-5 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Richieste di preventivo
            </h1>
          </Reveal>

          {state.status === "loading" && (
            <p className="mt-14 text-slate-400" data-testid="admin-loading">Caricamento…</p>
          )}

          {state.status === "needAuth" && (
            <div className="mt-14 trace-card rounded-2xl bg-[#0E1726] p-10 text-center" data-testid="admin-login-prompt">
              <Lock size={28} className="text-[#D4AF37] mx-auto" />
              <p className="mt-5 text-slate-300">Accedi con il tuo account Google per vedere le richieste.</p>
              <button
                data-testid="admin-login-button"
                onClick={loginWithGoogle}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-6 py-3 rounded-full"
              >
                Accedi con Google
              </button>
            </div>
          )}

          {state.status === "forbidden" && (
            <div className="mt-14 trace-card rounded-2xl bg-[#0E1726] p-10 text-center" data-testid="admin-forbidden">
              <Lock size={28} className="text-[#D4AF37] mx-auto" />
              <p className="mt-5 text-slate-300">Questa area è riservata all'amministratore del sito.</p>
              <p className="mt-2 text-sm text-slate-500">Hai effettuato l'accesso con un account non autorizzato.</p>
            </div>
          )}

          {state.status === "error" && (
            <p className="mt-14 text-slate-400" data-testid="admin-error">Errore nel caricamento delle richieste.</p>
          )}

          {state.status === "ok" && state.quotes.length === 0 && (
            <div className="mt-14 trace-card rounded-2xl bg-[#0E1726] p-10 text-center" data-testid="admin-empty">
              <Inbox size={28} className="text-[#D4AF37] mx-auto" />
              <p className="mt-5 text-slate-300">Nessuna richiesta per ora.</p>
              <p className="mt-2 text-sm text-slate-500">Quando un visitatore compila il modulo preventivo, la troverai qui.</p>
            </div>
          )}

          {state.status === "ok" && state.quotes.length > 0 && (
            <div className="mt-10 flex flex-col gap-5" data-testid="admin-quotes-list">
              {state.quotes.map((q, i) => (
                <Reveal key={q.quote_id} delay={0.05 * i}>
                  <article className="trace-card rounded-2xl bg-[#0E1726] p-6 sm:p-7" data-testid={`admin-quote-${q.quote_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h2 className="font-serif text-xl font-medium text-white">{q.nome}</h2>
                        <span className="mt-2 inline-block text-[11px] uppercase tracking-[0.2em] font-semibold text-[#F3E08B] bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full px-3 py-1">
                          {q.tipo}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">
                          {new Date(q.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                        <button
                          data-testid={`admin-quote-delete-${q.quote_id}`}
                          onClick={() => remove(q.quote_id)}
                          aria-label="Elimina richiesta"
                          className="text-slate-500 hover:text-red-400 border border-white/10 hover:border-red-400/50 rounded-full p-2 transition-colors"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-4 text-sm">
                      <a href={`mailto:${q.email}`} data-testid={`admin-quote-email-${q.quote_id}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-[#F3E08B] transition-colors">
                        <Mail size={14} className="text-[#D4AF37]" />{q.email}
                      </a>
                      {q.telefono && (
                        <a href={`tel:${q.telefono}`} data-testid={`admin-quote-phone-${q.quote_id}`} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-[#F3E08B] transition-colors">
                          <Phone size={14} className="text-[#D4AF37]" />{q.telefono}
                        </a>
                      )}
                    </div>
                    <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed whitespace-pre-line">{q.messaggio}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
